/**
 * CMS application.
 *
 * Lives in its own route tree and its own bundle chunk. Everything is gated: an unauthenticated
 * visitor gets the sign-in screen, and each route additionally checks the capability it needs, so
 * a URL typed directly cannot bypass the sidebar's visibility rules.
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import { AdminLayout } from '@/layouts/AdminLayout';
import { RequireAuth, RequireCapability } from '@/components/admin/Guards';
import { LoginPage } from '@/pages/admin/LoginPage';
import { DashboardPage } from '@/pages/admin/DashboardPage';
import { ContentListPage } from '@/pages/admin/ContentListPage';
import { ContentEditPage } from '@/pages/admin/ContentEditPage';
import { LeadsPage } from '@/pages/admin/LeadsPage';
import { ApplicationsPage } from '@/pages/admin/ApplicationsPage';
import { MediaPage } from '@/pages/admin/MediaPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { AuditPage } from '@/pages/admin/AuditPage';
import { ProfilePage } from '@/pages/admin/ProfilePage';

export function AdminApp() {
  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="profile" element={<ProfilePage />} />

        {/* One generic list/edit pair serves every workflow-controlled content type. */}
        <Route path=":resource" element={<ContentListPage />} />
        <Route path=":resource/new" element={<ContentEditPage />} />
        <Route path=":resource/:slug" element={<ContentEditPage />} />

        <Route
          path="leads"
          element={
            <RequireCapability capability="can_manage_leads">
              <LeadsPage />
            </RequireCapability>
          }
        />
        <Route
          path="careers/applications"
          element={
            <RequireCapability capability="can_manage_applications">
              <ApplicationsPage />
            </RequireCapability>
          }
        />
        <Route path="media" element={<MediaPage />} />
        <Route
          path="settings"
          element={
            <RequireCapability capability="can_manage_settings">
              <SettingsPage />
            </RequireCapability>
          }
        />
        <Route
          path="users"
          element={
            <RequireCapability capability="can_manage_users">
              <UsersPage />
            </RequireCapability>
          }
        />
        <Route
          path="audit"
          element={
            <RequireCapability capability="can_view_audit">
              <AuditPage />
            </RequireCapability>
          }
        />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}

export default AdminApp;
